/* eslint-disable import/no-cycle */
import { Button } from '@mui/material';
import React, { useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import RHFInput from 'src/components/elements/Input/RHFInput';
import RHFMultiAutoComplete from 'src/components/elements/MultiAutoComplete/RHFMultiAutoComplete';
import RHFTextArea from 'src/components/elements/TextArea/RHFTextArea';
import { useLocations } from 'src/hooks/query/locations';
import useSkills from 'src/hooks/query/skills';
import useYupValidationResolver from 'src/hooks/useYupValidationResolver';
import * as yup from 'yup';

interface DiceDetailsFormProps {
  jobTitle: string;
  skills: [];
  fetchCandidatesForDice: (data: DiceDetailsFormStateProps) => Promise<void>;
}

export interface DiceDetailsFormStateProps {
  jobTitle: string;
  location: [];
  skills: [];
  booleanSearch: {},
}

const validationSchema = yup.object().shape({
  jobTitle: yup.string().typeError('Enter Job Title').required('Job Title Required'),
  skills: yup.array().min(1, 'Select at least 1 skill').required('Skills Required'),
});

const DiceDetailsForm = function DiceDetailsForm(props: DiceDetailsFormProps) {
  const {
    jobTitle, skills, fetchCandidatesForDice,
  } = props;

  const resolver = useYupValidationResolver(validationSchema);
  const methods = useForm<DiceDetailsFormStateProps>({
    resolver,
    defaultValues: {
      jobTitle,
      skills,
    },
  });

  const {
    handleSubmit,
  } = methods;

  const submitHandler: SubmitHandler<DiceDetailsFormStateProps> = async (data) => {
    fetchCandidatesForDice(data);
  };

  useEffect(() => {
    methods.setValue('jobTitle', jobTitle);
  }, [jobTitle]);

  useEffect(() => {
    methods.setValue('skills', skills);
  }, [skills]);

  const {
    locationsOptions,
    locationsLoading,
    fetchLocations,
  } = useLocations({ enabled: false });

  const {
    skillsOptions,
    skillsLoading,
    fetchSkills,
  } = useSkills({ enabled: false });

  useEffect(() => {
    if (locationsOptions.length === 0) fetchLocations();
    if (skillsOptions.length === 0) fetchSkills();
  }, []);

  return (
    <form className="w-full flex flex-col items-start gap-5 custom-shadow p-3 my-5" onSubmit={handleSubmit(submitHandler)}>
      <div className="w-full flex-shrink-0 flex flex-wrap items-start gap-3">
        <div className="w-[300px] flex-shrink-0">
          <RHFInput name="jobTitle" control={methods.control} label="Job Title" />
        </div>
        <div className="w-[400px] flex-shrink-0">
          <RHFMultiAutoComplete
            control={methods.control}
            name="location"
            label="Job Location"
            options={locationsOptions || []}
            loading={locationsLoading}
            placeholder="Search Location"
          />
        </div>
        <div className="w-[500px] flex-shrink-0">
          <RHFMultiAutoComplete
            name="skills"
            label="Skills"
            control={methods.control}
            options={skillsOptions || []}
            loading={skillsLoading}
            placeholder="Search Skills"
          />
        </div>
      </div>
      <div className="w-[500px] items-start">
        <RHFTextArea
          name="booleanSearch"
          control={methods.control}
          label="Boolean Search"
          rows={4}
        />
      </div>
      <div className="flex gap-3">
        <Button
          variant="contained"
          className="h-[40px] w-[150px] font-medium whitespace-nowrap rounded-lg"
          size="small"
          type="submit"
        >
          Search
        </Button>
        <Button
          variant="outlined"
          className="h-[40px] w-[150px] font-medium whitespace-nowrap rounded-lg"
          size="small"
          type="reset"
          onClick={() => { methods.reset({}); }}
        >
          Reset
        </Button>
      </div>
    </form>
  );
};

DiceDetailsForm.defaultProps = {

};

DiceDetailsForm.propTypes = {

};

export default DiceDetailsForm;
